/**
 * The undo manifest: what was applied on each step, in what order, how long it
 * took, and where its pre-apply socket lives.
 *
 * This is the small, serialisable half of cross-device undo — it rides in the
 * step checkpoint metadata, while the heavy pre-apply meshes live as separate
 * blobs (see `beforeMeshCache` / `beforeMeshUploader`).
 *
 * Shape per step:
 *
 *   {
 *     stepToolIds,                // tools the step declared at capture time
 *     toolCount,                  // their count (legacy manifests carry only this)
 *     entries: [                  // one per apply, in apply order
 *       { toolId, seq, at, durationMs, vertexCount, strategy }
 *     ],
 *     beforeByTool: {             // write-once per (step, tool)
 *       [toolId]: { kind, key?, bytes?, vertexCount?, at, uploaded }
 *     },
 *     firstToolId, lastToolId,    // the two step-level pointers
 *   }
 *
 * **Why the manifest records the step's tool list.** `TOOL_IDS_BY_STEP` answers
 * "which tools are on this step" for *this build*. Undo and the backward revert
 * need "which tools were on this step when the work was done", and a deploy that
 * reassigns a tool between steps makes those two different answers. Recording
 * `stepToolIds` at capture time makes each manifest self-describing, so a design
 * that was in progress across such a deploy is still reverted against the flow it
 * was actually built with — see `docs/UNDO_ARCHITECTURE.md`.
 *
 * A count alone could not do this: swapping two tools between steps, or moving one
 * out while another moves in, leaves the count unchanged and the drift undetected.
 * `toolCount` is still written so an older build reading a newer manifest keeps
 * the coarse check it understands.
 *
 * **Why `beforeByTool` is write-once.** A tool re-applied with new settings
 * (thickness nudged from 4mm to 5mm three times) keeps the mesh from before its
 * *first* apply in this step. That matches what undo actually does downstream:
 * `resetApplied` returns the card to fully un-applied, so undo means "this tool
 * was never applied here", not "step back one nudge". It also means a slider
 * tweak never re-uploads a mesh.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { GLOBAL_STEP_ORDER } from '../../../shared/constants/flowConfig';
import { TOOL_IDS_BY_STEP } from '../../../shared/constants/tools';
import { strategyForTool } from './undoStrategy';

/**
 * Bump when the per-step manifest shape below changes *incompatibly*.
 *
 * Deliberately still 1 after `stepToolIds` was added: the field is additive and
 * optional, so a new build reading an old manifest degrades to the count check
 * (see {@link diffToolList}) and an old build reading a new one ignores the field.
 * Bumping instead would make `hydrateStep` reject every manifest already written,
 * costing every in-progress design its cross-device undo to gain nothing.
 */
export const UNDO_MANIFEST_SCHEMA_VERSION = 1;

/** Where a pre-apply socket can be found. */
export const BEFORE_KIND = {
  /** A dedicated blob written for this (step, tool). */
  BLOB: 'blob',
  /**
   * No blob needed — this was the first apply on the step, so the pre-apply
   * socket is the previous step's finished checkpoint, which is already stored.
   */
  PREV_STEP_CHECKPOINT: 'prev_step_checkpoint',
};

function emptyStep(stepId) {
  const declared = TOOL_IDS_BY_STEP[stepId] ?? [];
  return {
    schemaVersion: UNDO_MANIFEST_SCHEMA_VERSION,
    stepToolIds: [...declared],
    toolCount: declared.length,
    entries: [],
    beforeByTool: {},
    firstToolId: null,
    lastToolId: null,
  };
}

/**
 * Compare the tool list a manifest was recorded against with the one this build
 * declares for the same step.
 *
 * `departed` is the interesting half: those tools are no longer part of this step,
 * so any pre-apply mesh recorded for them here describes a socket from a different
 * position in the flow. `hydrateStep` drops their before-refs rather than letting
 * the resolver pick one.
 *
 * @param {string[]|null} recordedToolIds  null for a legacy count-only manifest
 * @param {number} recordedCount
 * @param {string[]} declaredNow
 * @returns {{ drift: boolean, departed: string[], arrived: string[] }}
 */
function diffToolList(recordedToolIds, recordedCount, declaredNow) {
  if (Array.isArray(recordedToolIds)) {
    const declared = new Set(declaredNow);
    const recorded = new Set(recordedToolIds);
    const departed = recordedToolIds.filter((id) => !declared.has(id));
    const arrived = declaredNow.filter((id) => !recorded.has(id));
    return { drift: departed.length > 0 || arrived.length > 0, departed, arrived };
  }
  // Legacy manifest: only a count was recorded, so an equal-size reassignment is
  // undetectable. Report what it can still tell us and nothing more.
  return {
    drift: Number.isFinite(recordedCount) && recordedCount !== declaredNow.length,
    departed: [],
    arrived: [],
  };
}

const useUndoManifestStore = create(
  devtools((set, get) => ({
    /** @type {Record<string, ReturnType<typeof emptyStep>>} */
    byStep: {},

    /**
     * Record one apply. Appends an entry and refreshes the step pointers.
     *
     * @param {string} stepId
     * @param {string} toolId
     * @param {{ durationMs?: number, vertexCount?: number }} [metrics]
     */
    recordApply: (stepId, toolId, { durationMs = null, vertexCount = null } = {}) =>
      set((state) => {
        if (!stepId || !toolId) return state;
        const step = state.byStep[stepId] ?? emptyStep(stepId);
        const entries = [
          ...step.entries,
          {
            toolId,
            seq: step.entries.length,
            at: Date.now(),
            durationMs,
            vertexCount,
            strategy: strategyForTool(toolId),
          },
        ];
        // Widen the recorded list to cover a tool added to this step since the
        // manifest was written. `getStepToolIds` unions with the live config for
        // *this* build, but the serialized record has to stand on its own — the
        // next build's `hydrateStep` has no other way to learn the tool was here.
        const recorded = step.stepToolIds ?? [];
        const stepToolIds = recorded.includes(toolId) ? recorded : [...recorded, toolId];
        return {
          byStep: {
            ...state.byStep,
            [stepId]: {
              ...step,
              stepToolIds,
              entries,
              firstToolId: entries[0].toolId,
              lastToolId: toolId,
            },
          },
        };
      }),

    /**
     * Note where this tool's pre-apply socket lives. Write-once: a second call
     * for the same (step, tool) is ignored so re-applies keep the original.
     *
     * @param {string} stepId
     * @param {string} toolId
     * @param {{ kind: string, key?: string, bytes?: number, vertexCount?: number, uploaded?: boolean }} ref
     * @returns {void}
     */
    setBeforeRef: (stepId, toolId, ref) =>
      set((state) => {
        if (!stepId || !toolId || !ref?.kind) return state;
        const step = state.byStep[stepId] ?? emptyStep(stepId);
        if (step.beforeByTool[toolId]) return state;
        return {
          byStep: {
            ...state.byStep,
            [stepId]: {
              ...step,
              beforeByTool: {
                ...step.beforeByTool,
                [toolId]: { ...ref, at: Date.now(), uploaded: !!ref.uploaded },
              },
            },
          },
        };
      }),

    /** Flip a ref to uploaded once the background PUT/finalize lands. */
    markBeforeUploaded: (stepId, toolId, patch = {}) =>
      set((state) => {
        const step = state.byStep[stepId];
        const ref = step?.beforeByTool?.[toolId];
        if (!ref) return state;
        return {
          byStep: {
            ...state.byStep,
            [stepId]: {
              ...step,
              beforeByTool: {
                ...step.beforeByTool,
                [toolId]: { ...ref, ...patch, uploaded: true },
              },
            },
          },
        };
      }),

    getStep: (stepId) => get().byStep[stepId] ?? null,

    /**
     * Every tool that could have work on this step: the list recorded when its
     * manifest was written **union** the list this build declares.
     *
     * A union rather than either one alone, because the two failure directions are
     * not symmetric. Recorded-only misses a tool *added* to the step since the
     * manifest was written (it was applied in this session but the frozen list
     * predates it). Declared-only misses a tool *removed* from the step, which is
     * the original drift bug. Both misses leave an applied card or a live snapshot
     * behind after a discard.
     *
     * Over-inclusion is free: every consumer is a no-op for a tool with nothing
     * applied and no snapshot. See {@link toolIdsForStep}.
     */
    getStepToolIds: (stepId) => {
      const recorded = get().byStep[stepId]?.stepToolIds;
      const declared = TOOL_IDS_BY_STEP[stepId] ?? [];
      if (!Array.isArray(recorded) || recorded.length === 0) return declared;
      const seen = new Set(recorded);
      return [...recorded, ...declared.filter((id) => !seen.has(id))];
    },

    getEntriesForStep: (stepId) => get().byStep[stepId]?.entries ?? [],

    getBeforeRef: (stepId, toolId) => get().byStep[stepId]?.beforeByTool?.[toolId] ?? null,

    hasBeforeRef: (stepId, toolId) => !!get().byStep[stepId]?.beforeByTool?.[toolId],

    /** True when this tool has not been applied on this step yet. */
    isFirstApplyOnStep: (stepId, toolId) => {
      const step = get().byStep[stepId];
      if (!step) return true;
      return !step.entries.some((e) => e.toolId === toolId);
    },

    /** True when nothing at all has been applied on this step yet. */
    isStepUntouched: (stepId) => (get().byStep[stepId]?.entries?.length ?? 0) === 0,

    /** `{ firstToolId, lastToolId }` — the step-level undo pointers. */
    getStepPointers: (stepId) => {
      const step = get().byStep[stepId];
      return {
        firstToolId: step?.firstToolId ?? null,
        lastToolId: step?.lastToolId ?? null,
      };
    },

    /** Total recorded apply time for a step, in ms (nulls ignored). */
    getStepDurationMs: (stepId) =>
      (get().byStep[stepId]?.entries ?? []).reduce(
        (sum, e) => sum + (Number.isFinite(e.durationMs) ? e.durationMs : 0),
        0,
      ),

    /**
     * Entries applied *after* `toolId`'s **first** apply on this step — the work
     * that an undo of `toolId` would also discard. Drives the confirmation dialog.
     *
     * First rather than most-recent, because that is what every undo source
     * restores: the before-mesh is written once (before the first apply), the
     * in-memory snapshot is captured once for the same reason, and `resetApplied`
     * returns the card to un-applied rather than to its previous settings. Keying
     * off the last apply would claim later tools survived work that the restored
     * geometry predates.
     *
     * A tool applied twice therefore has its own later entries in this list; they
     * are filtered out by the caller, which is undoing that tool anyway.
     *
     * @param {string} stepId
     * @param {string} toolId
     * @returns {{ toolId: string, seq: number }[]}
     */
    getEntriesAfterTool: (stepId, toolId) => {
      const entries = get().byStep[stepId]?.entries ?? [];
      const idx = entries.findIndex((e) => e.toolId === toolId);
      if (idx < 0) return [];
      return entries.slice(idx + 1);
    },

    /**
     * Undo bookkeeping: drop `toolId`'s first entry and everything applied after
     * it, mirroring the history truncation `resetHistoryUpTo` performs. Their
     * before-refs go too, since those tools are no longer applied.
     *
     * Cuts at the *first* apply of `toolId` — see `getEntriesAfterTool` for why.
     *
     * @param {string} stepId
     * @param {string} toolId
     * @returns {string[]} tool ids that were dropped (excluding `toolId`)
     */
    truncateAtTool: (stepId, toolId) => {
      const step = get().byStep[stepId];
      if (!step) return [];
      const entries = step.entries;
      const idx = entries.findIndex((e) => e.toolId === toolId);
      if (idx < 0) return [];

      const kept = entries.slice(0, idx);
      const dropped = entries.slice(idx);
      const droppedToolIds = [...new Set(dropped.map((e) => e.toolId))];
      // A dropped tool keeps its before-ref only if an earlier surviving entry
      // still has it applied.
      const stillApplied = new Set(kept.map((e) => e.toolId));
      const beforeByTool = {};
      for (const [id, ref] of Object.entries(step.beforeByTool)) {
        if (!droppedToolIds.includes(id) || stillApplied.has(id)) beforeByTool[id] = ref;
      }

      set({
        byStep: {
          ...get().byStep,
          [stepId]: {
            ...step,
            entries: kept.map((e, i) => ({ ...e, seq: i })),
            beforeByTool,
            firstToolId: kept[0]?.toolId ?? null,
            lastToolId: kept[kept.length - 1]?.toolId ?? null,
          },
        },
      });

      return droppedToolIds.filter((id) => id !== toolId && !stillApplied.has(id));
    },

    /** Serialisable manifest for one step — goes into the checkpoint metadata. */
    serializeForStep: (stepId) => {
      const step = get().byStep[stepId];
      if (!step || step.entries.length === 0) return null;
      return {
        schemaVersion: UNDO_MANIFEST_SCHEMA_VERSION,
        stepId,
        stepToolIds: step.stepToolIds ?? TOOL_IDS_BY_STEP[stepId] ?? [],
        toolCount: step.toolCount,
        entries: step.entries,
        beforeByTool: step.beforeByTool,
        firstToolId: step.firstToolId,
        lastToolId: step.lastToolId,
        totalDurationMs: get().getStepDurationMs(stepId),
      };
    },

    /**
     * Rebuild a step's manifest from a server/cache record. In-memory wins — it
     * is the live one.
     *
     * @param {object|null} manifest
     * @returns {boolean} whether anything was hydrated
     */
    hydrateStep: (manifest) => {
      if (!manifest?.stepId || !Array.isArray(manifest.entries)) return false;
      if (manifest.schemaVersion !== UNDO_MANIFEST_SCHEMA_VERSION) {
        console.warn(
          `[UndoManifest] ignoring ${manifest.stepId} — schema ${manifest.schemaVersion}`,
        );
        return false;
      }
      const existing = get().byStep[manifest.stepId];
      if (existing?.entries?.length) return false;

      const declaredNow = TOOL_IDS_BY_STEP[manifest.stepId] ?? [];
      const recordedToolIds = Array.isArray(manifest.stepToolIds)
        ? manifest.stepToolIds
        : null;
      const { drift, departed } = diffToolList(
        recordedToolIds,
        manifest.toolCount,
        declaredNow,
      );

      // A departed tool's pre-apply mesh describes the socket at its position in
      // the *old* flow. Undoing to it from wherever the tool lives now would land
      // the design on geometry from a different point in the workflow, so the ref
      // goes and the resolver falls through to a source scoped to the step it is
      // actually being asked about.
      const beforeByTool = { ...(manifest.beforeByTool ?? {}) };
      for (const toolId of departed) delete beforeByTool[toolId];

      if (drift) {
        console.warn(
          `[UndoManifest] ${manifest.stepId} was saved against a different tool list ` +
            `(${recordedToolIds ? recordedToolIds.length : manifest.toolCount} tools, ` +
            `this build declares ${declaredNow.length}) — recipe undo is gated off for it` +
            (departed.length ? `; dropped before-refs for ${departed.join(', ')}` : ''),
        );
      }

      set({
        byStep: {
          ...get().byStep,
          [manifest.stepId]: {
            schemaVersion: manifest.schemaVersion,
            // The recorded list is the historical truth and outranks this build's
            // config for every "which tools were on this step" question.
            stepToolIds: recordedToolIds ?? [...declaredNow],
            toolCount: manifest.toolCount ?? declaredNow.length,
            toolListDrift: drift,
            entries: manifest.entries,
            beforeByTool,
            firstToolId: manifest.firstToolId ?? manifest.entries[0]?.toolId ?? null,
            lastToolId:
              manifest.lastToolId ??
              manifest.entries[manifest.entries.length - 1]?.toolId ??
              null,
          },
        },
      });
      return true;
    },

    /** True when this step's manifest came from a build with a different tool list. */
    hasToolListDrift: (stepId) => !!get().byStep[stepId]?.toolListDrift,

    clearStep: (stepId) =>
      set((state) => {
        if (!state.byStep[stepId]) return state;
        const { [stepId]: _drop, ...rest } = state.byStep;
        return { byStep: rest };
      }),

    /** Drop manifests for every step after `stepId` (backward revert). */
    clearStepsAfter: (stepId) =>
      set((state) => {
        const idx = GLOBAL_STEP_ORDER.indexOf(stepId);
        if (idx < 0) return state;
        const keep = new Set(GLOBAL_STEP_ORDER.slice(0, idx + 1));
        const next = {};
        for (const [step, manifest] of Object.entries(state.byStep)) {
          if (keep.has(step)) next[step] = manifest;
        }
        return { byStep: next };
      }),

    /** Keep only the listed steps (post-export stage pruning). */
    retainOnlySteps: (stepIds) =>
      set((state) => {
        const keep = new Set(stepIds ?? []);
        const next = {};
        for (const [step, manifest] of Object.entries(state.byStep)) {
          if (keep.has(step)) next[step] = manifest;
        }
        return { byStep: next };
      }),

    clearAll: () => set({ byStep: {} }),
  })),
);

/**
 * The tools that belong to `stepId` **for undo purposes** — the list recorded in
 * the step's manifest when its work was done, or this build's config when the step
 * has no manifest (nothing applied there yet).
 *
 * Every caller that is about to un-apply, clear or serialize "this step's tools"
 * must use this rather than `TOOL_IDS_BY_STEP` directly. Those callers all act on
 * work that has *already happened*, and a deploy that reassigned a tool between
 * steps makes today's config the wrong answer about it: the discard would leave
 * the moved tool's card claiming to be applied and its stale snapshot reachable
 * from an Undo button. `TOOL_IDS_BY_STEP` stays correct for rendering, which is
 * about the flow as it is now.
 *
 * @param {string} stepId
 * @returns {string[]}
 */
export function toolIdsForStep(stepId) {
  return useUndoManifestStore.getState().getStepToolIds(stepId);
}

export default useUndoManifestStore;
